import { Formik, Form, Field } from 'formik';
import idx from 'idx';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { ButtonWithLoadingIcon } from '../../components/common/ButtonWithLoadingIcon';
import { NotificationManager } from '../../components/common/react-notifications';
import { Status } from '../../constants/status';
import { useUserContext } from '../../context/UserContext';
import productAPI from '../../services/API/product';
import './rentals.scss';
import moment from 'moment';

const fitOptions = [
  { label: "Didn't fit", value: 'Uncomfortable' },
  { label: "Didn't look right", value: 'Item was not I expected' },
  { label: 'Arrived damaged', value: 'Item was damaged when I received it' },
  { label: 'Other', value: 'Other' },
];

const wearFrequency = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5+', value: 5 },
];

const FormRowScaffold = ({
  label,
  id,
  showError,
  error,
  children,
  fieldDescription = '',
  isOptional,
  ...other
}) => {
  return (
    <FormGroup>
      <div className='d-flex flex-direction-row align-items-center'>
        <Label
          for={id}
          {...other}
          style={{ fontFamily: 'Brown Bold', color: '#2E2B2B' }}
        >
          {label}
          {isOptional ? (
            <Label
              for={id}
              {...other}
              style={{
                fontFamily: 'Brown',
                opacity: 0.7,
                color: '#2E2B2B',
                paddingLeft: 6,
              }}
            >
              {'(Optional)'}
            </Label>
          ) : null}
        </Label>
      </div>
      <div style={{ flexDirection: 'column', display: 'flex' }}>
        {children}
        {showError && (
          <div
            className='invalid-feedback d-block'
            style={{ fontFamily: 'Brown' }}
          >
            {error}
          </div>
        )}

        {fieldDescription && (
          <small className='text-muted mb-2 mt-2 d-block'>
            {fieldDescription}
          </small>
        )}
      </div>
    </FormGroup>
  );
};

const SelectFormRow = ({
  label,
  name,
  id,
  options,
  showError,
  error,
  showEmpty,
  onChange,
  isOptional,
  ...otherProps
}) => {
  return (
    <FormRowScaffold
      label={label}
      id={id}
      {...otherProps}
      isOptional
      showError={showError}
      error={error}
    >
      <Field
        as='select'
        name={name}
        id={id}
        className='form-control'
        onChange={onChange}
      >
        {showEmpty && (
          <option value='' disabled>
            Select
          </option>
        )}
        {options.map((opt, index) => (
          <option value={opt.value} key={`${name}_${index}`}>
            {opt.label ? opt.label : opt.display}
          </option>
        ))}
      </Field>
    </FormRowScaffold>
  );
};

const CommonInputFormRow = ({
  label,
  name,
  id,
  type,
  showError,
  error,
  isOptional,
  ...otherProps
}) => (
  <FormRowScaffold
    label={label}
    id={id}
    showError={showError}
    error={error}
    isOptional={isOptional}
  >
    <Field
      type={type}
      name={name}
      id={id}
      className='form-control'
      {...otherProps}
    />
  </FormRowScaffold>
);

export const RentalReturnModal = ({
  productData,
  onClose,
  updateProductStatus,
}) => {
  const { shop } = useUserContext();
  const initialValues = {
    wear_frequency: '',
    user_reasons: [],
    user_note: '',
  };

  const validationSchema = Yup.object().shape({
    wear_frequency: Yup.number().required('Please select wear frequency'),
    user_note: Yup.string().when('user_reasons', {
      is: (user_reasons) =>
        user_reasons.includes('Item was damaged when I received it') ||
        user_reasons.includes('Other'),
      then: Yup.string().required('Please enter note'),
    }),
  });

  const [issuesList, setIssuesList] = useState([]);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [selectedWearOption, setSelectedWearOption] = useState(null);
  const [leftReview, setLeftReview] = useState('NO');
  const [returnNote, setReturnNote] = useState('');
  const [isFitReturn, setIsFitReturn] = useState(false);

  const { user } = useUserContext();
  const [returning, setReturning] = useState(false);

  const hasReturnStarted =
    productData.status === Status.returning_with_complaint ||
    productData.status === Status.returned_c ||
    productData.status === Status.fit_returning_r ||
    productData.status === Status.returning_c ||
    productData.status === Status.returning_r;

  const targetDate =
    shop?.fit_return_days &&
    shop?.fit_return_days > 0 &&
    productData?.rental_delivery_date
      ? moment(productData.rental_delivery_date).subtract(
          shop.fit_return_days,
          'day'
        )
      : null;
  const formatedTargetDate = targetDate
    ? moment([
        new Date(targetDate).getFullYear(),
        new Date(targetDate).getMonth(),
        new Date(targetDate).getDate(),
      ])
    : null;
  const formatedRentalDeliveryDate = productData?.rental_delivery_date
    ? moment([
        new Date(productData.rental_delivery_date).getFullYear(),
        new Date(productData.rental_delivery_date).getMonth(),
        new Date(productData.rental_delivery_date).getDate(),
      ])
    : null;
  const differenceInDays =
    formatedRentalDeliveryDate && formatedTargetDate
      ? formatedRentalDeliveryDate.diff(formatedTargetDate, 'd')
      : 0;
  const newFitOptions =
    differenceInDays && differenceInDays === 1
      ? [
          { label: 'This is a fit return', value: 'Uncomfortable' },
          { label: "Didn't look right", value: 'Item was not I expected' },
          {
            label: 'Arrived damaged',
            value: 'Item was damaged when I received it',
          },
          { label: 'Other', value: 'Other' },
        ]
      : fitOptions;

  useEffect(() => {
    let filterData = {};
    productAPI
      .getIssues(filterData)
      .then((issue) => {
        setIssuesList(issue);
      })
      .catch(() => {});
    const dataToSend = {
      product_id: productData.id,
      shopify_product_id: productData.shopify_product_id || null,
    };
    productAPI
      .getReview(dataToSend)
      .then((resultData) => {
        const reviewsData = idx(resultData, (_) => _.reviews) || [];
        setLeftReview(reviewsData.length > 0 ? 'YES' : 'NO');
      })
      .catch(() => {});
  }, []);

  const handleStartReturn = async (values) => {
    if (values?.wear_frequency !== '') {
      const returnReasonsData = {
        product_id: productData.id,
        user_id: user.id,
        user_reasons: values.user_reasons,
        wear_frequency: values.wear_frequency,
      };
      const status = {
        id: productData.id,
        user_id: user.id,
        status: isFitReturn
          ? Status.fit_returning_r
          : selectedIssues.length > 0
          ? Status.returning_with_complaint
          : Status.returning_r,
      };

      const returnNoteObj = {
        product_id: productData.id,
        user_id: user.id,
        user_note: values.user_note,
      };
      setReturning(true);
      try {
        await productAPI.addRentalReturnReason(
          returnReasonsData,
          async (res) => {
            if (res.status === 200) {
              await productAPI.addRentalReturnNote(
                returnNoteObj,
                async (returnRes) => {
                  if (returnRes.status === 200) {
                    await updateProductStatus(status, '', (updateRes) => {
                      if (updateRes.status === 200) {
                        setReturning(false);
                        NotificationManager.success({
                          message: 'Return has been successfully started',
                        });
                        onClose(true);
                      } else {
                        setReturning(false);
                      }
                    });
                  } else {
                    setReturning(false);
                  }
                  setReturning(false);
                }
              );
            } else {
              setReturning(false);
            }
          }
        );
      } catch (error) {
        setReturning(false);
        NotificationManager.error({
          message: 'Something went wrong, please try again',
        });
      }
    }
  };

  // return (
  //   <>
  //     {hasReturnStarted ? (
  //       <h2 style={{ color: '#C98B76', fontFamily: 'Brown' }}>
  //         You have already Started the return
  //       </h2>
  //     ) : (
  //       <div>
  //         <PDSection title='Product Info' mbClass={'mb-1'} mbBodyClass={'mb-1'}>
  //           <BasicDetailItem label='ID' value={productData.id} />
  //           <BasicDetailItem label='Title' value={productData.title} />
  //         </PDSection>

  //         {/* <PDSection title='Your Review'> */}
  //         <div className={`basic-detail-item vertical mb-1`}>
  //           <div className='basic-detail-label'>
  //             {`Left Review: ${leftReview}`}
  //           </div>
  //         </div>
  //         <div className={`basic-detail-item vertical`}>
  //           <div className='basic-detail-label'>
  //             If any problems, select them below
  //           </div>
  //           <div className='basic-detail-data mt-2'>
  //             <div className='problems'>
  //               {issuesList.length > 0 ? (
  //                 issuesList.map((issue, index) => (
  //                   <CustomInput
  //                     type='checkbox'
  //                     id={issue.id}
  //                     label={issue.issue}
  //                     value={issue.id}
  //                     checked={issue.isChecked}
  //                     onChange={(e) => {
  //                       handleIssueSelection(index, e.target.checked);
  //                       // onCheckChanged(e.target.checked);
  //                     }}
  //                   />
  //                 ))
  //               ) : (
  //                 <Skeleton width={200} height={150} />
  //               )}
  //             </div>
  //           </div>
  //         </div>

  //         <div className={`basic-detail-item vertical`}>
  //           <div className='basic-detail-label'>Return Note </div>
  //           <div className='basic-detail-data mt-2'>
  //             <Input
  //               type='textarea'
  //               name='returnNote'
  //               value={returnNote}
  //               onChange={(e) => {
  //                 setReturnNote(e.target.value);
  //               }}
  //             />
  //           </div>
  //         </div>

  //         <div className='basic-detail-data mt-2'>
  //           <CustomInput
  //             type='checkbox'
  //             label='This is a fit return'
  //             id='fit-return'
  //             value={isFitReturn}
  //             onChange={(e) => {
  //               setIsFitReturn(e.target.checked);
  //             }}
  //             // value={issue.id}/>
  //           />
  //         </div>
  //         {/* </PDSection> */}

  //         <div className='d-flex justify-content-end mt-4'>
  //           {canSubmit ? (
  //             <ButtonWithLoadingIcon
  //               disabled={returning}
  //               type='submit'
  //               loading={returning}
  //               color='primary'
  //               size='sm'
  //               onClick={handleStartReturn}
  //             >
  //               START RETURN
  //             </ButtonWithLoadingIcon>
  //           ) : (
  //             <Skeleton width={200} height={50} />
  //           )}
  //         </div>
  //       </div>
  //     )}
  //   </>
  // );

  const renderButtons = () => {
    return (
      <div
        className={'review-button'}
        style={{
          border: 'none',
        }}
      >
        <ButtonWithLoadingIcon
          style={{
            alignSelf: 'center',
            width: '50%',
            marginBottom: 10,
            borderColor: '#2E2B2B',
            backgroundColor: returning ? '#EFEB75' : null,
            color: returning ? '#2E2B2B' : null,
          }}
          disabled={returning}
          loading={returning}
          type={'submit'}
          color={'primary'}
        >
          SUBMIT
        </ButtonWithLoadingIcon>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div
        className='d-flex'
        style={{
          backgroundColor: '#F9E4D7',
          color: '#2E2B2B',
        }}
      >
        <div className={'mr-3'} style={{ height: 140, width: 110 }}>
          <img
            src={productData.img_url}
            alt={productData.title}
            style={{ height: '100%', width: '100%' }}
            loading={'lazy'}
          />
        </div>
        <div
          className='d-flex'
          style={{
            width: '100%',
            paddingRight: 15,
            paddingTop: 5,
            flexDirection: 'column',
          }}
        >
          <div
            className='d-flex align-items-center'
            style={{ justifyContent: 'space-between' }}
          >
            <span style={{ fontFamily: 'Brown', fontSize: 16 }}>
              {productData?.brand?.toUpperCase() || '--'}
            </span>
            <span style={{ fontFamily: 'Brown', fontSize: 16 }}>
              Order{' '}
              {productData.order_number ? `#${productData.order_number}` : '--'}
            </span>
          </div>
          <span className='mb-2' style={{ fontFamily: 'Brown', fontSize: 20 }}>
            {productData.title || productData.product_title || ''}
          </span>
          <span style={{ fontSize: 15 }}>
            {`${productData?.size ?? ''}/${productData?.color ?? ''}`}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      {hasReturnStarted ? (
        <h2 style={{ color: '#2E2B2B', fontFamily: 'Brown' }}>
          Hey there, bestie! It looks like you've already started your return.
        </h2>
      ) : (
        <div
          style={{
            marginBottom: 15,
            marginTop: 15,
            color: '#2E2B2B',
          }}
        >
          {renderHeader()}

          <div
            style={{
              marginTop: 30,
            }}
          >
            <Formik
              enableReinitialize={true}
              initialValues={initialValues}
              onSubmit={handleStartReturn}
              validationSchema={validationSchema}
            >
              {({
                values,
                errors,
                isSubmitting,
                touched,
                setFieldValue,
                setFieldTouched,
              }) => {
                return (
                  <Form className='mb-4'>
                    <FormGroup>
                      <Label
                        for={'wear_frequency'}
                        style={{ fontFamily: 'Brown Bold' }}
                      >
                        {'How many time did you wear this item?'}
                      </Label>
                      <div
                        style={{
                          marginBottom: 25,
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            flexDirection: 'row',
                            display: 'flex',
                          }}
                        >
                          {wearFrequency.map((wf, index) => {
                            return (
                              <div
                                onMouseOver={() => {
                                  setFieldValue('wear_frequency', wf.value);
                                }}
                                className={'times-to-wear-bubble'}
                                style={{
                                  marginRight:
                                    index === wearFrequency.length - 1 ? 0 : 10,
                                  backgroundColor:
                                    values.wear_frequency === wf.value
                                      ? '#EFEB75'
                                      : 'transparent',
                                }}
                                name={'wear_frequency'}
                                onClick={() => {
                                  setFieldValue('wear_frequency', wf.value);
                                }}
                              >
                                <span style={{ fontSize: 15 }}>{wf.label}</span>
                              </div>
                            );
                          })}
                        </div>
                        <span
                          style={{
                            fontSize: '0.76rem',
                            color: '#F96E4E',
                          }}
                        >
                          {errors.wear_frequency}
                        </span>
                      </div>
                    </FormGroup>
                    <div style={{ marginBottom: 25 }}>
                      <SelectFormRow
                        label='If there was an issue with your item, please let us know.'
                        name='user_reasons'
                        isOptional
                        id='user_reasons'
                        options={newFitOptions || []}
                        onChange={(e) => {
                          setFieldValue('user_reasons', [e.target.value]);
                          const selectedOpt = newFitOptions.find(
                            (o) => o.value === e.target.value
                          )?.label;
                          if (selectedOpt === 'This is a fit return') {
                            setIsFitReturn(true);
                          } else {
                            setIsFitReturn(false);
                          }
                        }}
                        value={values.user_reasons}
                        onBlur={setFieldTouched}
                        error={errors.user_reasons}
                        showEmpty
                      />
                    </div>

                    <div style={{ marginBottom: 25 }}>
                      <CommonInputFormRow
                        label='Return note'
                        isOptional={
                          values?.user_reasons?.length > 0 &&
                          !values?.user_reasons?.includes(
                            'Item was damaged when I received it'
                          ) &&
                          !values?.user_reasons?.includes('Other')
                        }
                        type='text'
                        name='user_note'
                        id='user_note'
                        showError={errors.user_note && touched.user_note}
                        error={errors.user_note}
                        component='textarea'
                        rows={4}
                        style={{ borderRadius: 10 }}
                        isInvalid={errors.user_note && touched.user_note}
                      />
                    </div>
                    {renderButtons()}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};
